import * as THREE from 'three';
import { bus } from '../engine/EventBus';
import { save } from '../engine/SaveSystem';
import type { Terrain } from './Terrain';

export interface Landmark {
  id: 'library' | 'cafe' | 'shop' | 'home';
  name: string;
  position: THREE.Vector3;
  triggerRadius: number;
  onInteract: () => void;
  group: THREE.Group;
}

function mat(color: string, rough = 0.9): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: rough });
}

function buildLibrary(): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(new THREE.BoxGeometry(8, 4, 6), mat('#d8d0c0'));
  base.position.y = 2;
  base.castShadow = true; base.receiveShadow = true;
  g.add(base);
  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(6.2, 3, 4),
    mat('#9970dd'),
  );
  roof.rotation.y = Math.PI / 4;
  roof.position.y = 5.5;
  roof.castShadow = true;
  g.add(roof);
  // Windows
  for (const x of [-2.5, 0, 2.5]) {
    const w = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 1.2, 0.1),
      new THREE.MeshStandardMaterial({ color: '#ffe8a8', emissive: '#ffd98a', emissiveIntensity: 0.4 }),
    );
    w.position.set(x, 2.3, 3.01);
    g.add(w);
  }
  // Door
  const door = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2.0, 0.1), mat('#6f4a3a'));
  door.position.set(0, 1, 3.05);
  g.add(door);
  // Sign
  const sign = new THREE.Mesh(new THREE.BoxGeometry(3, 0.6, 0.1), mat('#ff7aa8'));
  sign.position.set(0, 3.6, 3.1);
  g.add(sign);
  return g;
}

function buildCafe(): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(new THREE.BoxGeometry(7, 3.5, 6), mat('#ffd9e6'));
  base.position.y = 1.75;
  base.castShadow = true; base.receiveShadow = true;
  g.add(base);
  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(5.5, 2.5, 16),
    mat('#ff7aa8'),
  );
  roof.position.y = 4.75;
  roof.castShadow = true;
  g.add(roof);
  const awning = new THREE.Mesh(
    new THREE.BoxGeometry(7.5, 0.15, 2),
    mat('#fff6e5'),
  );
  awning.position.set(0, 2.6, 3.5);
  g.add(awning);
  // Outdoor table
  const table = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 0.1, 18), mat('#d8a87a'));
  table.position.set(3, 0.9, 4.5);
  table.castShadow = true;
  g.add(table);
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.9, 8), mat('#b08060'));
  pole.position.set(3, 0.45, 4.5);
  g.add(pole);
  const door = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2, 0.1), mat('#6f4a3a'));
  door.position.set(0, 1, 3.01);
  g.add(door);
  return g;
}

function buildShop(): THREE.Group {
  const g = new THREE.Group();
  // Mushroom-shaped shop
  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(2.2, 2.6, 3.5, 20),
    mat('#fff0d7'),
  );
  stem.position.y = 1.75;
  stem.castShadow = true; stem.receiveShadow = true;
  g.add(stem);
  const cap = new THREE.Mesh(
    new THREE.SphereGeometry(4, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2),
    mat('#ff7aa8', 0.7),
  );
  cap.position.y = 3.4;
  cap.castShadow = true;
  g.add(cap);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.35, 10, 8), mat('#ffffff'));
    dot.position.set(Math.cos(a) * 2.6, 3.8 + Math.sin(i) * 0.2, Math.sin(a) * 2.6);
    g.add(dot);
  }
  const door = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2, 0.1), mat('#6f4a3a'));
  door.position.set(0, 1, 2.3);
  g.add(door);
  const sign = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.5, 0.1), mat('#9970dd'));
  sign.position.set(0, 2.8, 2.35);
  g.add(sign);
  return g;
}

function buildHouse(): THREE.Group {
  const g = new THREE.Group();
  // Floor
  const floor = new THREE.Mesh(new THREE.BoxGeometry(8, 0.2, 8), mat('#d8b891'));
  floor.position.y = 0.1;
  floor.receiveShadow = true;
  g.add(floor);

  const wallMat = mat('#ffe8d6');
  const wallT = 0.2;
  const wallH = 3;

  // Back wall
  const back = new THREE.Mesh(new THREE.BoxGeometry(8, wallH, wallT), wallMat);
  back.position.set(0, wallH / 2 + 0.1, -4);
  back.castShadow = true; back.receiveShadow = true;
  g.add(back);
  // Side walls
  for (const x of [-4, 4]) {
    const side = new THREE.Mesh(new THREE.BoxGeometry(wallT, wallH, 8), wallMat);
    side.position.set(x, wallH / 2 + 0.1, 0);
    side.castShadow = true; side.receiveShadow = true;
    g.add(side);
  }
  // Front wall with door cutout — two segments
  const front1 = new THREE.Mesh(new THREE.BoxGeometry(3, wallH, wallT), wallMat);
  front1.position.set(-2.5, wallH / 2 + 0.1, 4);
  g.add(front1);
  const front2 = new THREE.Mesh(new THREE.BoxGeometry(3, wallH, wallT), wallMat);
  front2.position.set(2.5, wallH / 2 + 0.1, 4);
  g.add(front2);
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(2, 0.8, wallT), wallMat);
  lintel.position.set(0, wallH - 0.3, 4);
  g.add(lintel);

  // Roof (pyramid)
  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(6.2, 2.2, 4),
    mat('#ff7aa8'),
  );
  roof.rotation.y = Math.PI / 4;
  roof.position.y = wallH + 0.1 + 1.1;
  roof.castShadow = true;
  g.add(roof);

  // Welcome mat
  const m = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.05, 0.8), mat('#a47bff'));
  m.position.set(0, 0.22, 4.5);
  g.add(m);

  // Warm interior light
  const warm = new THREE.PointLight('#ffd98a', 0.7, 10, 1.8);
  warm.position.set(0, 2.5, 0);
  g.add(warm);
  return g;
}

export class Landmarks {
  readonly group = new THREE.Group();
  readonly items: Landmark[] = [];
  readonly homePlot: { x: number; z: number; y: number };

  constructor(terrain: Terrain) {
    const pos = terrain.getLandmarkPositions();
    this.homePlot = terrain.getHousePlot();

    const lib = buildLibrary();
    lib.position.set(pos.library.x, pos.library.y, pos.library.z);
    this.group.add(lib);
    this.items.push({
      id: 'library',
      name: 'The Moonwhisper Library',
      position: new THREE.Vector3(pos.library.x, pos.library.y, pos.library.z + 3),
      triggerRadius: 3.5,
      group: lib,
      onInteract: () => {
        bus.emit('toast', { message: '📖 The librarian smiles. "A new book arrived — about kind dragons."' });
      },
    });

    const cafe = buildCafe();
    cafe.position.set(pos.cafe.x, pos.cafe.y, pos.cafe.z);
    this.group.add(cafe);
    this.items.push({
      id: 'cafe',
      name: 'Honeypetal Cafe',
      position: new THREE.Vector3(pos.cafe.x, pos.cafe.y, pos.cafe.z + 3),
      triggerRadius: 3.5,
      group: cafe,
      onInteract: () => {
        const today = new Date().toDateString();
        if (save.data.lastTreatDay !== today) {
          save.data.lastTreatDay = today;
          save.data.coins += 5;
          bus.emit('coins:changed', { coins: save.data.coins });
          bus.emit('save:dirty', {});
          bus.emit('toast', { message: '🍰 A warm berry tart — the cafe gifts you 5 coins!' });
        } else {
          bus.emit('toast', { message: '☕ "Back so soon? Rest your wings a while."' });
        }
      },
    });

    const shop = buildShop();
    shop.position.set(pos.shop.x, pos.shop.y, pos.shop.z);
    this.group.add(shop);
    this.items.push({
      id: 'shop',
      name: 'Glimmerbloom Shop',
      position: new THREE.Vector3(pos.shop.x, pos.shop.y, pos.shop.z + 2.5),
      triggerRadius: 3.5,
      group: shop,
      onInteract: () => bus.emit('mode:enter', { mode: 'shop' }),
    });

    const home = buildHouse();
    home.position.set(this.homePlot.x, this.homePlot.y, this.homePlot.z);
    this.group.add(home);
    this.items.push({
      id: 'home',
      name: 'Your Cottage',
      position: new THREE.Vector3(this.homePlot.x, this.homePlot.y, this.homePlot.z),
      triggerRadius: 4.5,
      group: home,
      onInteract: () => bus.emit('toast', { message: '🏡 Press B inside your cottage to decorate!' }),
    });
  }

  nearest(playerPos: THREE.Vector3): Landmark | null {
    let best: Landmark | null = null;
    let bestD = Infinity;
    for (const lm of this.items) {
      const d = playerPos.distanceTo(lm.position);
      if (d < lm.triggerRadius && d < bestD) { best = lm; bestD = d; }
    }
    return best;
  }

  isOnHomePlot(p: THREE.Vector3): boolean {
    return Math.abs(p.x - this.homePlot.x) < 4 && Math.abs(p.z - this.homePlot.z) < 4;
  }
}
